import { api } from '../services/api'
import type { SchoolClass } from '../types'
import { CATEGORY_ART, CATEGORY_ORDER } from '../components/public/subjectArt'

type Result = { ok: true; data: SchoolClass[] } | { ok: false; error: unknown }
let pending: { started: number; result: Promise<Result> } | undefined
let warmStarted = false

function anonymousIdentity(): boolean {
    if (api.getAccessToken()) return false
    try { return localStorage.getItem('dhtn-has-session') !== '1' } catch { return false }
}

/** The cover the catalog will show first, so it can be fetched before React lays out shelves. */
function firstCoverUrl(data: SchoolClass[]): string | undefined {
    const available = data.filter(sc => sc.isPublished || sc.isEnrolled)
    const selected = CATEGORY_ORDER.find(key => key === new URLSearchParams(window.location.search).get('category'))
    const firstCategory = selected ?? CATEGORY_ORDER.find(key => available.some(sc => sc.category === key))
    const first = firstCategory ? available.find(sc => sc.category === firstCategory) : available[0]
    return first?.thumbnail?.trim() || (first?.category ? CATEGORY_ART[first.category] : undefined)
}

/** Start the anonymous cold catalog GET while React initializes its shell. */
export function warmPublicCatalog(): void {
    // An existing session must finish auth initialization before its own GET.
    if (warmStarted || !['/classes', '/classes/'].includes(window.location.pathname) || !anonymousIdentity()) return
    warmStarted = true
    const started = Date.now()
    pending = {
        started,
        // Keep rejection handled until the mounted page consumes the result.
        result: api.getClasses().then(data => {
            try { if (anonymousIdentity() && Date.now() - started <= 10000 && ['/classes', '/classes/'].includes(window.location.pathname)) {
                const url = firstCoverUrl(data)
                if (url) {
                    const cover = new Image()
                    cover.fetchPriority = 'high'; cover.decoding = 'async'; cover.src = url
                }
            } } catch { /* Image warming must never replace a successful catalog response. */ }
            return { ok: true, data }
        }, error => ({ ok: false, error })),
    }
}

/** One use only, same anonymous identity, and never a later navigation cache. */
export async function takePublicCatalogWarmup(userId: number | string | undefined): Promise<SchoolClass[]> {
    const request = pending
    pending = undefined
    const reusable = () => !!request && userId === undefined && anonymousIdentity() && Date.now() - request.started <= 10000
    if (!request || !reusable()) return api.getClasses()
    const result = await request.result
    // Login may have completed while the anonymous request was in flight.
    if (!reusable()) return api.getClasses()
    if (!result.ok) throw result.error
    return result.data
}
